import moment from "moment-timezone";
import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import Button from "../../../../../../../../../components/Button";
import Field from "../../../../../../../../../components/Field";
import Checkbox from "../../../../../../../../../components/Inputs/Checkbox";
import DurationInput, { getDurationInputValue } from "../../../../../../../../../components/Inputs/DurationInput";
import HourTypeSelect from "../../../../../../../../../components/Inputs/HourTypeSelect";
import ResourceSelect from "../../../../../../../../../components/Inputs/ResourceSelect";
import Select from "../../../../../../../../../components/Inputs/Select";
import { renderDurationAsFormat } from "../../../../../../../../../utils/helpers/date";
import {
	getPathWithParams,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../../../../../../utils/helpers/settings";
import { payCodeCalcFields } from "../../../../../helper";
import PayCodeSelect from "../../../PayCodeSelect";

const WEEKDAYS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

function Details({ close, guarantee, disabled }) {
	const { t } = useTranslation();

	const [hidden, setHidden] = useState(false);

	const onChange = useCallback(
		(e) => setHidden(!e.target.checked),
		[setHidden],
	);

	return (
		<Form
			name="daily-guarantee"
			onFinish={close}
			labelAlign="left"
			labelCol={{ span: 8 }}
			wrapperCol={{ span: 14, offset: 2 }}
			colon={false}
			initialValues={guarantee}
			scrollToFirstError
		>
			<Field
				className="my-3"
				name="baseOnExpectedHours"
				valuePropName="checked"
			>
				<Checkbox id="base-on-expected-hours">
					{t('base-on-expected-hours')}
				</Checkbox>
			</Field>

			<Field
				name="weekdays"
				label={t('weekdays')}
				rules={[
					{
						required: true,
						message: t("at-least-1-weekday-required"),
					},
				]}
			>
				<Select mode="multiple">
					{WEEKDAYS.map((day) => (
						<Select.Option key={day} value={day}>
							{t(day)}
						</Select.Option>
					))}
				</Select>
			</Field>

			<Field
				name="minWorkedDuration"
				label={t('minimum-worked-duration')}
			>
				{({ value, onChange }) => (
					<DurationInput
						value={value ? renderDurationAsFormat(value, 'HH:mm') : undefined}
						timeFormat="HH:mm"
						className="time-input"
						onChange={(value) => {
							onChange(getDurationInputValue(value));
						}}
					/>
				)}
			</Field>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue }) => {
					const minWorkedDuration = getFieldValue("minWorkedDuration");
					if (minWorkedDuration) {
						return null;
					}
					return (
						<Field
							name="initializeByClock"
							valuePropName="checked"
						>
							<Checkbox id="initialize-by-clock">
								{t('initialize-by-clock')}
							</Checkbox>
						</Field>
					);
				}}
			</BaseField>

			<Field
				name="duration"
				label={t('duration')}
				rules={[
					{
						required: true,
						message: t('duration-is-required'),
					},
					({ getFieldValue }) => ({
						validator(_, value) {
							const minWorkedDuration = Number(getFieldValue("minWorkedDuration"));
							value = Number(value);
							if (isNaN(value) || isNaN(minWorkedDuration) || value === 0) {
								return Promise.resolve();
							}
							if (value <= minWorkedDuration) {
								return Promise.reject(new Error(t('duration-bigger-than-min-worked-duration')));
							}
							return Promise.resolve();
						},
					}),
				]}
				dependencies={["minWorkedDuration"]}
			>
				{({ value, onChange }) => (
					<DurationInput
						value={value ? renderDurationAsFormat(value, 'HH:mm') : undefined}
						timeFormat="HH:mm"
						className="time-input"
						onChange={(value) => {
							value = getDurationInputValue(value);
							if (value >= moment.duration(24, 'hours').asSeconds()) {
								value = moment.duration(24, 'hours').asSeconds() - moment.duration(1, 'minute').asSeconds();
							}
							if (value < moment.duration(1, 'minute').asSeconds()) {
								value = moment.duration(1, 'minute').asSeconds();
							}
							onChange(value);
						}}
					/>
				)}
			</Field>

			<div className="bg-lighter rounded p-3 mb-4">
				<BaseField shouldUpdate noStyle>
					{({ }, { }, { getFieldValue, setFieldValue }) => {
						const includeWorkedDuration = !!getFieldValue("includeWorkedDuration");

						return (
							<>
								<Field name="includeWorkedDuration" className="mb-0">
									{({ value, onChange }) => {
										const checked = !!value;
										return (
											<Checkbox
												id="include-worked-duration"
												onChange={(e) => {
													const value = e.target.checked ? {} : null;
													onChange(value);
													const cap = getFieldValue("cap");
													if (cap) {
														setFieldValue(["cap", "daily"], false);
													}
												}}
												checked={checked}
											>
												{t('include-worked-duration')}
											</Checkbox>
										);
									}}
								</Field>

								{includeWorkedDuration && (
									<>
										<Field name="cap" valuePropName="checked" className="mt-4">
											{({ checked, onChange }) => {
												return (
													<Checkbox
														id="cap"
														checked={checked}
														onChange={(e) => {
															const value = e.target.checked ? {} : false;
															onChange(value);
														}}
													>
														{t('cap')}
													</Checkbox>
												);
											}}
										</Field>

										<BaseField shouldUpdate noStyle>
											{({ }, { }, { getFieldValue }) => {
												const includeWorkedDuration = getFieldValue("includeWorkedDuration");
												const cap = getFieldValue("cap");

												if (!cap) {
													return null;
												}

												return (
													<div>
														{includeWorkedDuration && (
															<Field name={["cap", "daily"]} valuePropName="checked">
																<Checkbox id="cap-daily">
																	{t('cap-daily-worked-duration')}
																</Checkbox>
															</Field>
														)}

														<Row>
															{["weekly", "biweekly", "semi-monthly", "monthly"].map((frequency) => {
																return (
																	<Col md={6}>
																		<Field
																			key={frequency}
																			name={["cap", frequency]}
																			label={t(frequency) + ' ' + t('cap').toLowerCase()}
																		>
																			{({ value, onChange }) => (
																				<DurationInput
																					value={value ? renderDurationAsFormat(value, 'HH:mm') : undefined}
																					timeFormat="HH:mm"
																					className="time-input"
																					onChange={(value) => {
																						value = getDurationInputValue(value);
																						onChange(value);
																					}}
																				/>
																			)}
																		</Field>
																	</Col>
																);
															})}
														</Row>
													</div>
												);
											}}
										</BaseField>

										<PayCodeSelect
											label="included-pay-codes"
											name={["includeWorkedDuration", "payCodes"]}
											disabled={disabled}
										/>
									</>
								)}
							</>
						);
					}}
				</BaseField>
			</div>

			<Field
				name="payCode"
				label={t("pay-code")}
				rules={[
					{
						required: true,
						message: t("required-pay-code"),
					},
				]}
			>
				<ResourceSelect
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
					renderLabel={renderSettingsResourceLabel}
					resourcePath={getPathWithParams('/pay-code', true, undefined, payCodeCalcFields, { hourType: 'unit' })}
					placeholder={t("pay-code")}
					disabled={disabled}
				/>
			</Field>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue, setFieldValue }) => {
					const payCodes = getFieldValue("payCodes");
					const onChange = (hourTypes) => {
						setFieldValue("payCodes", payCodes?.filter((payCode) =>
							hourTypes.includes(payCode.hourType),
						));
					};

					return (
						<Field
							label={t("eligible-hour-types")}
							name="hourTypes"
						>
							<HourTypeSelect
								mode="multiple"
								placeholder={t("eligible-hour-types")}
								onChange={onChange}
								disabled={disabled}
							/>
						</Field>
					);
				}}
			</BaseField>

			<div className="mb-3">
				<Checkbox
					checked={!hidden}
					onChange={onChange}
					id="show-pay-code"
					disabled={disabled}
				>
					{t("show-eligible-pay-code")}
				</Checkbox>
			</div>

			{!hidden && (
				<BaseField shouldUpdate>
					{({ }, { }, { getFieldValue }) => (
						<PayCodeSelect
							hourTypes={getFieldValue("hourTypes")}
							name="payCodes"
							disabled={disabled}
						/>
					)}
				</BaseField>
			)}

			<div className="d-flex justify-content-between my-2">
				{disabled ? (
					<div />
				) : (
					<Button
						type="submit"
						color="primary"
						size="xs"
						className="bg-dark"
					>
						{t("save-changes")}
					</Button>
				)}

				<Button
					size="small"
					className="border-primary-color mr-1"
					onClick={close}
				>
					{t("close")}
				</Button>
			</div>
		</Form>
	);
}

export default Details;
